import React, { useState, useEffect, useRef } from "react";
import { db, API_BASE_URL } from "../../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import {
  FaEye,
  FaTrash,
  FaPlus,
  FaEdit,
  FaShareAlt,
  FaFilter,
  FaSpinner,
} from "react-icons/fa";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, ROLES, FEATURES } from "../../lib/rolesConfig";

const formatDateForDisplay = (timestamp) => {
  if (!timestamp) return "N/A";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat("en-CA").format(date); // YYYY-MM-DD
};

const PrescriptionManager = () => {
  const { currentUser, activeCareProfile, userRole } = useAuth();
  const [prescriptions, setPrescriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPrescription, setCurrentPrescription] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    physician: "",
    date: "",
    description: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileData, setSelectedFileData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const formRef = useRef(null);
  const [currentFileName, setCurrentFileName] = useState("");

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(null); // Track which item is loading

  const isOwner = activeCareProfile?.userId === currentUser?.uid;
  const role =
    userRole && activeCareProfile ? userRole[activeCareProfile.id] : null;
  const effectiveRole = isOwner ? ROLES.OWNER : role;

  const canCreate = hasPermission(
    effectiveRole,
    FEATURES.PRESCRIPTION,
    "write",
  );
  const canUpdate = hasPermission(
    effectiveRole,
    FEATURES.PRESCRIPTION,
    "write",
  );
  const canDelete = hasPermission(
    effectiveRole,
    FEATURES.PRESCRIPTION,
    "write",
  );
  const canShare = hasPermission(effectiveRole, FEATURES.PRESCRIPTION, "write"); // Using 'write' as a proxy for share

  useEffect(() => {
    if (!activeCareProfile) {
      setIsLoading(false);
      setPrescriptions([]);
      return;
    }

    setIsLoading(true);
    const q = query(
      collection(db, "prescriptions"),
      where("careProfileId", "==", activeCareProfile.id),
      orderBy("lastModifiedAt", sortOrder),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const fetchedPrescriptions = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPrescriptions(fetchedPrescriptions);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching prescriptions: ", error);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [activeCareProfile, sortOrder]);

  const filteredPrescriptions = prescriptions.filter((p) => {
    const name = p.prescriptionName || p.medicationName || "";
    return (
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.physicianName &&
        p.physicianName.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        setSelectedFileData({ base64, name: file.name, type: file.type });
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedFileData(null);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", physician: "", date: "", description: "" });
    setSelectedFile(null);
    setIsEditing(false);
    setCurrentPrescription(null);
    setCurrentFileName("");
  };

  const toggleForm = () => {
    if (!canCreate && !showForm) {
      alert("You don't have permission to add prescriptions.");
      return;
    }
    setShowForm(!showForm);
    resetForm();
  };

  const handleEdit = (prescription) => {
    if (!canUpdate) {
      alert("You don't have permission to edit prescriptions.");
      return;
    }
    setIsEditing(true);
    setCurrentPrescription(prescription);
    setCurrentFileName(prescription.fileName || "");
    setFormData({
      name: prescription.prescriptionName || prescription.medicationName,
      physician: prescription.physicianName || "",
      date: formatDateForDisplay(
        prescription.prescriptionDate || prescription.prescribedDate,
      ),
      description: prescription.description || "",
    });
    setShowForm(true);
    setTimeout(
      () => formRef.current?.scrollIntoView({ behavior: "smooth" }),
      100,
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const permission = isEditing ? canUpdate : canCreate;
    if (!permission) {
      alert("You don't have permission to perform this action.");
      return;
    }
    if (!formData.name || !formData.date || (!selectedFile && !isEditing)) {
      alert("Name, date, and a file are required.");
      return;
    }

    setIsUploading(true);

    const prescriptionData = {
      prescriptionName: formData.name,
      physicianName: formData.physician,
      prescriptionDate: formData.date,
      description: formData.description || "",
    };

    try {
      let finalPayload = { ...prescriptionData };

      if (selectedFileData) {
        finalPayload.fileData = selectedFileData;
      }

      const idToken = await currentUser.getIdToken();

      let url = "";
      let body = {};

      if (isEditing) {
        url = `${API_BASE_URL}/prescriptions/updatePrescription`;
        body = {
          careProfileId: activeCareProfile.id,
          prescriptionId: currentPrescription.id,
          prescriptionData: finalPayload,
        };
      } else {
        url = `${API_BASE_URL}/prescriptions/createPrescription`;
        body = {
          careProfileId: activeCareProfile.id,
          ...finalPayload,
        };
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Server error");
      }

      setSelectedFileData(null);

      alert(`Prescription ${isEditing ? "updated" : "uploaded"} successfully!`);
      toggleForm();
    } catch (error) {
      console.error("Error handling prescription:", error);
      alert(
        `Failed to ${isEditing ? "update" : "upload"} prescription. ${error.message}`,
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (prescription) => {
    if (!canDelete) {
      alert("You don't have permission to delete prescriptions.");
      return;
    }
    if (
      window.confirm(
        "Are you sure you want to delete this prescription? This is permanent.",
      )
    ) {
      try {
        const idToken = await currentUser.getIdToken();
        const response = await fetch(
          `${API_BASE_URL}/prescriptions/deletePrescription`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              careProfileId: activeCareProfile.id,
              prescriptionId: prescription.id,
              storagePath: prescription.storagePath,
            }),
          },
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || "Server error");
        }

        alert("Prescription deleted.");
      } catch (error) {
        console.error("Error deleting prescription:", error);
        alert(`Failed to delete prescription. ${error.message}`);
      }
    }
  };

  const handlePreview = async (prescription) => {
    if (!canShare) {
      alert("You don't have permission to preview this file.");
      return;
    }
    if (!prescription.storagePath) {
      alert("This prescription does not have a viewable file.");
      return;
    }

    setIsGeneratingPreview(prescription.id);
    setPreviewUrl(null); // Close any existing preview

    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${API_BASE_URL}/prescriptions/generatePrescriptionSignedUrl`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            filePath: prescription.storagePath,
            careProfileId: activeCareProfile.id,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Server error");
      }

      const result = await response.json();
      const url = result.url || result.signedUrl;

      const fileType =
        prescription.fileName?.split(".").pop().toLowerCase() || "";
      if (["jpg", "jpeg", "png", "gif"].includes(fileType)) {
        setPreviewType("image");
      } else {
        setPreviewType("embed"); // For PDFs and other embeddable types
      }
      setPreviewUrl(url);
    } catch (error) {
      console.error("Error generating signed URL:", error);
      alert(`Error generating preview link. ${error.message}`);
    } finally {
      setIsGeneratingPreview(null);
    }
  };

  const closePreview = () => setPreviewUrl(null);

  const handleShare = async (prescription) => {
    if (!canShare) {
      alert("You don't have permission to share this file.");
      return;
    }
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${API_BASE_URL}/prescriptions/sharePrescription`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            filePath: prescription.storagePath,
            careProfileId: activeCareProfile.id,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Server error");
      }

      const result = await response.json();
      const url = result.url;

      if (navigator.share) {
        navigator
          .share({
            title: `Prescription for ${prescription.prescriptionName}`,
            text: `Here is the prescription for ${prescription.prescriptionName}.`,
            url: url,
          })
          .then(() => console.log("Successful share"))
          .catch((error) => console.log("Error sharing", error));
      } else {
        navigator.clipboard.writeText(url);
        alert("Sharable link copied to clipboard!");
      }
    } catch (error) {
      console.error("Error sharing prescription:", error);
      alert("Error creating sharable link. Please try again later.");
    }
  };

  if (isLoading)
    return <div className="text-center p-4">Loading prescriptions...</div>;
  if (!activeCareProfile)
    return (
      <div className="text-center p-4 text-red-500">
        Please select a care profile.
      </div>
    );

  return (
    <div className="bg-white p-4 rounded-lg shadow-md">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-4">
        <h2 className="text-2xl font-bold text-gray-700">Prescriptions</h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg shadow-sm hover:bg-gray-300 transition"
          >
            <FaFilter className="inline mr-2" />
            {showFilters ? "Hide" : "Show"} Filters
          </button>
          {canCreate && (
            <button
              onClick={toggleForm}
              className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
            >
              <FaPlus className="inline -ml-1 mr-2" />
              {showForm ? "Cancel" : "Add Prescription"}
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="flex flex-col md:flex-row gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
          <input
            type="text"
            placeholder="Search by name or doctor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-1/3 p-2 border rounded-lg"
          />
          <div className="flex items-center">
            <label className="mr-2">Sort by date: </label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="p-2 border rounded-lg"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>
      )}

      {showForm && (
        <div ref={formRef} className="p-4 mb-6 bg-gray-50 rounded-lg border">
          <h3 className="text-xl font-semibold mb-4">
            {isEditing ? "Edit" : "Upload"} Prescription
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Prescription Name*"
              className="w-full p-2 border rounded"
              required
            />
            <input
              type="text"
              name="physician"
              value={formData.physician}
              onChange={handleInputChange}
              placeholder="Physician's Name"
              className="w-full p-2 border rounded"
            />
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              className="w-full p-2 border rounded"
              required
            />
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Description (optional)"
              className="w-full p-2 border rounded"
            />
            <input type="file" onChange={handleFileChange} className="w-full" />
            {isEditing && currentFileName && (
              <p className="mt-2 text-sm text-gray-500">
                Current file: {currentFileName}. Uploading a new file will
                replace it.
              </p>
            )}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={toggleForm}
                className="bg-gray-300 px-4 py-2 rounded w-full sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-500 text-white px-4 py-2 rounded w-full sm:w-auto"
                disabled={isUploading}
              >
                {isUploading
                  ? "Saving..."
                  : isEditing
                    ? "Update Prescription"
                    : "Save Prescription"}
              </button>
            </div>
          </form>
        </div>
      )}

      {previewUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50">
          <div className="bg-white p-4 rounded-lg max-w-4xl max-h-[90vh] w-full h-full flex flex-col">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-xl font-bold">File Preview</h3>
              <button
                onClick={closePreview}
                className="text-red-500 font-bold text-2xl"
              >
                &times;
              </button>
            </div>
            <div className="flex-grow overflow-auto">
              {previewType === "image" ? (
                <img
                  src={previewUrl}
                  alt="Prescription"
                  className="max-w-full max-h-full mx-auto"
                />
              ) : (
                <embed
                  src={previewUrl}
                  type="application/pdf"
                  className="w-full h-full"
                />
              )}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {filteredPrescriptions.map((p) => (
          <div
            key={p.id}
            className="bg-white border rounded-lg shadow-sm overflow-hidden"
          >
            <div className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">
                    {p.prescriptionName || p.medicationName}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Dr. {p.physicianName || "N/A"}
                  </p>
                  <p className="text-sm text-gray-600">
                    Prescribed on:{" "}
                    {p.prescriptionDate
                      ? formatDateForDisplay(p.prescriptionDate)
                      : p.prescribedDate || "N/A"}
                  </p>
                  {p.fileName && (
                    <p className="text-sm text-gray-500">File: {p.fileName}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    Last Modified: {formatDateForDisplay(p.lastModifiedAt)}
                    {p.lastModifiedBy?.uid !== activeCareProfile.userId &&
                      ` by ${p.lastModifiedBy?.name || "Unknown"}`}
                  </p>
                </div>
                <div className="flex-shrink-0 ml-4 hidden sm:flex">
                  {canShare && (
                    <button
                      onClick={() => handlePreview(p)}
                      className="p-2 rounded-full hover:bg-gray-200 disabled:opacity-50"
                      title="Preview"
                      disabled={isGeneratingPreview === p.id}
                    >
                      {isGeneratingPreview === p.id ? (
                        <FaSpinner className="animate-spin text-gray-600" />
                      ) : (
                        <FaEye className="text-gray-600" />
                      )}
                    </button>
                  )}
                  {canShare && (
                    <button
                      onClick={() => handleShare(p)}
                      className="p-2 rounded-full hover:bg-gray-200"
                      title="Share"
                    >
                      <FaShareAlt className="text-gray-600" />
                    </button>
                  )}
                  {canUpdate && (
                    <button
                      onClick={() => handleEdit(p)}
                      className="p-2 rounded-full hover:bg-gray-200"
                      title="Edit"
                    >
                      <FaEdit className="text-blue-500" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(p)}
                      className="p-2 rounded-full hover:bg-gray-200"
                      title="Delete"
                    >
                      <FaTrash className="text-red-500" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="p-2 bg-gray-50 rounded-b-lg border-t flex justify-end space-x-2 sm:hidden">
              {canShare && (
                <button
                  onClick={() => handlePreview(p)}
                  className="p-2 rounded-full hover:bg-gray-200 disabled:opacity-50"
                  title="Preview"
                  disabled={isGeneratingPreview === p.id}
                >
                  {isGeneratingPreview === p.id ? (
                    <FaSpinner className="animate-spin text-gray-600" />
                  ) : (
                    <FaEye className="text-gray-600" />
                  )}
                </button>
              )}
              {canShare && (
                <button
                  onClick={() => handleShare(p)}
                  className="p-2 rounded-full hover:bg-gray-200"
                  title="Share"
                >
                  <FaShareAlt className="text-gray-600" />
                </button>
              )}
              {canUpdate && (
                <button
                  onClick={() => handleEdit(p)}
                  className="p-2 rounded-full hover:bg-gray-200"
                  title="Edit"
                >
                  <FaEdit className="text-blue-500" />
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => handleDelete(p)}
                  className="p-2 rounded-full hover:bg-gray-200"
                  title="Delete"
                >
                  <FaTrash className="text-red-500" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {filteredPrescriptions.length === 0 && !isLoading && (
        <p className="text-center text-gray-500 py-4">
          No prescriptions found.
        </p>
      )}
    </div>
  );
};

export default PrescriptionManager;
